import React from 'react';

interface PendingClearancePageProps {
  userEmail?: string;
  onSignOut: () => void;
}

const PendingClearancePage: React.FC<PendingClearancePageProps> = ({ userEmail, onSignOut }) => {
  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, sans-serif',
      background: '#0f172a',
      color: '#f8fafc',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        .pcp-grid { position:fixed; inset:0; background-image:linear-gradient(rgba(56,130,246,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(56,130,246,0.04) 1px,transparent 1px); background-size:56px 56px; pointer-events:none; }
        .pcp-glow { position:fixed; inset:0; pointer-events:none; background:radial-gradient(ellipse 600px 400px at 50% 30%,rgba(59,130,246,0.07) 0%,transparent 60%); }
        .pcp-card { background:rgba(30,41,59,0.7); border:1px solid rgba(56,130,246,0.2); border-radius:20px; padding:2.5rem; max-width:520px; width:100%; text-align:center; backdrop-filter:blur(16px); position:relative; z-index:1; }
        .pcp-icon { font-size:3rem; margin-bottom:1rem; animation:pcpSpin 8s linear infinite; display:block; }
        @keyframes pcpSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        .pcp-badge { display:inline-flex; align-items:center; gap:6px; padding:.35rem .9rem; background:rgba(251,191,36,.1); border:1px solid rgba(251,191,36,.3); border-radius:999px; font-size:.72rem; font-weight:700; color:#fbbf24; text-transform:uppercase; letter-spacing:.08em; margin-bottom:1.25rem; }
        .pcp-pulse { width:7px; height:7px; background:#fbbf24; border-radius:50%; animation:pcpPulse 1.5s infinite; }
        @keyframes pcpPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(.7)} }
        .pcp-title { font-size:1.6rem; font-weight:900; letter-spacing:-.02em; margin-bottom:.75rem; }
        .pcp-sub { font-size:.93rem; color:#94a3b8; line-height:1.8; margin-bottom:1.75rem; }
        .pcp-steps { background:rgba(15,23,42,0.6); border:1px solid rgba(56,130,246,.15); border-radius:12px; padding:1.25rem; margin-bottom:1.5rem; text-align:left; }
        .pcp-step { display:flex; gap:.75rem; align-items:flex-start; margin-bottom:.85rem; }
        .pcp-step:last-child { margin-bottom:0; }
        .pcp-step-num { color:#3b82f6; font-weight:900; font-size:.8rem; min-width:20px; margin-top:1px; }
        .pcp-step-text { font-size:.84rem; color:#cbd5e1; line-height:1.6; }
        .pcp-step-text strong { color:#f8fafc; }
        .pcp-contact { background:rgba(56,130,246,.06); border:1px solid rgba(56,130,246,.15); border-radius:12px; padding:1.1rem 1.25rem; margin-bottom:1.5rem; text-align:left; }
        .pcp-contact-title { font-size:.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#3b82f6; margin-bottom:.6rem; }
        .pcp-contact a { color:#60a5fa; text-decoration:none; font-size:.87rem; }
        .pcp-contact p { color:#94a3b8; font-size:.87rem; margin:.2rem 0; }
        .pcp-btn { display:inline-block; padding:.7rem 1.5rem; border-radius:9px; font-size:.88rem; font-weight:700; text-decoration:none; cursor:pointer; transition:all .2s; }
        .pcp-btn-primary { background:linear-gradient(135deg,#3b82f6,#1d4ed8); color:#fff; box-shadow:0 4px 16px rgba(59,130,246,.35); border:none; width:100%; margin-bottom:.75rem; }
        .pcp-btn-primary:hover { transform:translateY(-1px); }
        .pcp-btn-ghost { background:none; border:1px solid rgba(56,130,246,.25); color:#94a3b8; width:100%; }
        .pcp-btn-ghost:hover { border-color:#3b82f6; color:#60a5fa; }
        .pcp-email-display { font-size:.8rem; color:#64748b; margin-top:1rem; }
      `}</style>

      <div className="pcp-grid" />
      <div className="pcp-glow" />

      <div className="pcp-card">
        <span className="pcp-icon">🔐</span>

        <div className="pcp-badge">
          <span className="pcp-pulse" />
          Account Verification In Progress
        </div>

        <h1 className="pcp-title">Clearance Pending</h1>
        <p className="pcp-sub">
          Your account has been created and is awaiting security clearance approval from the Eprims Ltd team. This typically takes <strong style={{ color: '#f8fafc' }}>1 business day</strong>.
        </p>

        <div className="pcp-steps">
          <div className="pcp-step">
            <span className="pcp-step-num">01</span>
            <p className="pcp-step-text"><strong>Account created</strong> — Your credentials have been securely stored. ✅</p>
          </div>
          <div className="pcp-step">
            <span className="pcp-step-num">02</span>
            <p className="pcp-step-text"><strong>Clearance review</strong> — An Eprims team member is reviewing your access request. ⏳</p>
          </div>
          <div className="pcp-step">
            <span className="pcp-step-num">03</span>
            <p className="pcp-step-text"><strong>Activation</strong> — Once approved, you'll have full access to your Intelligence Suites.</p>
          </div>
        </div>

        <div className="pcp-contact">
          <div className="pcp-contact-title">Need to expedite? Contact us directly</div>
          <p>📧 <a href="mailto:contact@eprims.com">contact@eprims.com</a></p>
          <p>📞 +1 (835) 290-9708</p>
          <p style={{ fontSize: '.78rem', color: '#475569', marginTop: '.4rem' }}>Mon–Fri · 9 AM–6 PM EST · Quote your registration email</p>
        </div>

        <a href="mailto:contact@eprims.com?subject=Account Clearance Request&body=Hi, I recently created an IntelOptics Insights account and am awaiting clearance. My email is: " className="pcp-btn pcp-btn-primary">
          📧 Email Us to Expedite →
        </a>
        <button onClick={onSignOut} className="pcp-btn pcp-btn-ghost">
          Sign Out
        </button>

        {userEmail && (
          <p className="pcp-email-display">Registered email: {userEmail}</p>
        )}
      </div>
    </div>
  );
};

export default PendingClearancePage;
